export type SheetDragEvent = 'pointermove' | 'pointerup' | 'pointercancel'

export interface SheetDragTarget {
  addEventListener(type: SheetDragEvent, listener: EventListener, options?: AddEventListenerOptions): void
  removeEventListener(type: SheetDragEvent, listener: EventListener): void
}

export interface SheetDragControllerOptions {
  getHeight: () => number
  setHeight: (height: number) => void
  setDragging: (dragging: boolean) => void
  minHeight: number
  maxHeight: number
  target?: SheetDragTarget
}

export function clampSheetHeight(height: number, minHeight: number, maxHeight: number): number {
  return Math.max(minHeight, Math.min(maxHeight, height))
}

/** Owns a bottom sheet's window-level pointer listeners and guarantees cleanup. */
export function createSheetDragController(options: SheetDragControllerOptions) {
  let removeActiveListeners: (() => void) | null = null

  const destroy = () => {
    removeActiveListeners?.()
    removeActiveListeners = null
    options.setDragging(false)
  }

  const onHandleDown = (event: PointerEvent) => {
    event.preventDefault()
    destroy()

    const target = options.target ?? window
    const startY = event.clientY
    const startHeight = options.getHeight()
    const onMove: EventListener = (nextEvent) => {
      const pointer = nextEvent as PointerEvent
      options.setHeight(
        clampSheetHeight(
          startHeight - (pointer.clientY - startY),
          options.minHeight,
          options.maxHeight,
        ),
      )
    }
    const onUp: EventListener = () => destroy()

    removeActiveListeners = () => {
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
      target.removeEventListener('pointercancel', onUp)
    }
    options.setDragging(true)
    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp, { once: true })
    // A pointer stream can END without a pointerup: an Android system edge gesture, a second
    // finger, or the WebView claiming the pointer all fire pointercancel instead. Listening
    // for only pointerup leaves `dragging` true and the pointermove listener on window, so
    // every later finger movement anywhere on screen keeps resizing the sheet.
    target.addEventListener('pointercancel', onUp, { once: true })
  }

  return { onHandleDown, destroy }
}

export interface ResizableSheetOptions<Default extends number | null = number> {
  minHeight: number
  /**
   * `null` means the sheet hugs its content (no `height` written) until the rider first drags
   * it — the Map Options sheet's behavior, where a two-row drill-down level must not cover
   * three quarters of the map with an empty panel.
   */
  defaultHeight: Default
  maxHeight: number
  /** Rendered height to start a drag from while `height` is still null. */
  measureHeight?: () => number
}

/** Svelte 5 adapter for the shared bottom-sheet drag behavior. */
export function useResizableSheet<Default extends number | null = number>(
  options: ResizableSheetOptions<Default>,
) {
  const clamp = (next: number) => clampSheetHeight(next, options.minHeight, options.maxHeight)
  let height = $state<number | null>(
    options.defaultHeight == null ? null : clamp(options.defaultHeight),
  )
  let dragging = $state(false)
  const drag = createSheetDragController({
    // A content-hugging sheet has no height of its own to drag from, so measure the rendered
    // one; without it the first drag would jump to minHeight.
    getHeight: () => height ?? clamp(options.measureHeight?.() ?? options.minHeight),
    setHeight: (next) => { height = next },
    setDragging: (next) => { dragging = next },
    minHeight: options.minHeight,
    maxHeight: options.maxHeight,
  })

  $effect(() => drag.destroy)

  return {
    get height() { return height as number | Default },
    // Accepts null only where `defaultHeight` does, i.e. on a content-hugging sheet: writing
    // one back to null hands sizing to the content again.
    set height(next: number | Default) {
      height = next == null ? null : clamp(next)
    },
    get dragging() { return dragging },
    onHandleDown: drag.onHandleDown,
    reset() { height = options.defaultHeight },
    toggleCollapse() {
      height = height != null && height <= options.minHeight + 10
        ? options.defaultHeight
        : options.minHeight
    },
  }
}
